import { cn } from '@seen/ui'
import { IconCheck, IconX } from '@tabler/icons-react'
import type { JSX } from 'react'
import { useId } from 'react'
import { Tooltip } from '#src/components/core/tooltip/Tooltip.tsx'
import { TooltipContent } from '#src/components/core/tooltip/TooltipContent.tsx'
import { TooltipTrigger } from '#src/components/core/tooltip/TooltipTrigger.tsx'

/* The Accept/Reject control for a pending AI suggestion.
 *
 * Presentational only — every surface that offers a decision renders this, so
 * an inline editor proposal and a care-plan questionnaire answer look the same
 * even though one accepts through the editor's proposal pipeline and the other
 * patches a QuestionnaireResponse. */
export function SuggestionActionsToolbar({
  onAccept,
  onReject,
  label,
  acceptDisabled = false,
  acceptDisabledReason,
  className,
}: {
  onAccept: () => void
  onReject: () => void
  // Trailing description of what accepting does, e.g. the text being replaced.
  label?: string | null
  acceptDisabled?: boolean
  // Why Accept is unavailable, shown on hover and on focus rather than beside
  // the button: a disabled control with no reason reads as a broken surface.
  acceptDisabledReason?: string | null
  className?: string
}): JSX.Element {
  const reasonId = useId()
  const refused = acceptDisabled && !!acceptDisabledReason
  const acceptButton = (
    <button
      type="button"
      aria-describedby={refused ? reasonId : undefined}
      className="flex items-center gap-1 rounded px-2 py-1 text-green-700 hover:bg-green-50 disabled:opacity-50 disabled:hover:bg-transparent dark:text-green-300 dark:hover:bg-green-950"
      disabled={acceptDisabled}
      onClick={onAccept}
    >
      <IconCheck className="size-3.5" />
      Accept
    </button>
  )

  return (
    <div
      role="toolbar"
      aria-label="AI proposal actions"
      className={cn(
        'flex items-center gap-1 rounded-md border bg-popover px-1.5 py-1 shadow-md',
        'text-popover-foreground text-xs',
        className
      )}
    >
      {refused ? (
        <Tooltip>
          {/* A disabled button fires neither pointer nor focus events, so the
              trigger is a focusable span around it — otherwise the reason is
              unreachable by keyboard and unavailable on hover. */}
          <TooltipTrigger asChild>
            {/* oxlint-disable-next-line jsx-a11y/no-noninteractive-tabindex -- the span is the tooltip's only reachable trigger, because the disabled button it wraps takes no focus of its own. */}
            <span className="inline-flex" tabIndex={0}>
              {acceptButton}
            </span>
          </TooltipTrigger>
          <TooltipContent>{acceptDisabledReason}</TooltipContent>
          {/* The tooltip alone is not dependable for a screen reader, which may
              never open it; the reason is tied to the button as a description
              so it is announced with the control either way. */}
          <span className="sr-only" id={reasonId}>
            {acceptDisabledReason}
          </span>
        </Tooltip>
      ) : (
        acceptButton
      )}
      <button
        type="button"
        className="flex items-center gap-1 rounded px-2 py-1 text-red-700 hover:bg-red-50 dark:text-red-300 dark:hover:bg-red-950"
        onClick={onReject}
      >
        <IconX className="size-3.5" />
        Reject
      </button>
      {!!label && (
        <span className="ml-1 border-l pl-2 text-muted-foreground">
          {label}
        </span>
      )}
    </div>
  )
}
